import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, doc, getDoc, query, where, orderBy, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';
import { translations } from '../../utils/translations';

function UserLeadsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { settings } = usePlatformSettings();
  
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('adminLanguage') || 'en';
  });
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetailDialog, setLeadDetailDialog] = useState(false);
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Selection and bulk actions state
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [bulkDeleteConfirmDialog, setBulkDeleteConfirmDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Notes state
  const [leadNotes, setLeadNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  
  // Reassign state
  const [allUsers, setAllUsers] = useState([]);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignTargetUser, setReassignTargetUser] = useState('');
  const [reassignSnackbar, setReassignSnackbar] = useState(false);
  
  const t = translations[language];
  
  // Filter leads based on search term
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase().trim();
    
    // Check name (case-insensitive)
    const nameMatch = (lead.name || '').toLowerCase().trim().includes(search);
    
    // Check email (case-insensitive)
    const emailMatch = (lead.email || '').toLowerCase().trim().includes(search);
    
    // Check phone (digits only comparison)
    const phoneDigits = (lead.phone || '').replace(/\D/g, '');
    const searchDigits = search.replace(/\D/g, '');
    const phoneMatch = searchDigits.length > 0 && phoneDigits.includes(searchDigits);
    
    return nameMatch || emailMatch || phoneMatch;
  });
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredLeads.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);
  
  // Reset to page 1 when search changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  
  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    localStorage.setItem('adminLanguage', language);
  }, [language]);

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage) {
      setLanguage(newLanguage);
    }
  };

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/admin');
        return;
      }

      setUser(user);

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) {
          navigate('/admin');
          return;
        }
        setUserProfile(userDoc.data());
      } catch (err) {
        console.error('Error loading user profile:', err);
        navigate('/admin');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load leads - admins see their leads + homepage leads, users see only their referrals
  const loadLeads = async () => {
    if (!userProfile) return;
    
    setLeadsLoading(true);
    try {
      const leadsRef = collection(db, 'leads');
      let q;
      
      console.log('Loading leads for user:', userProfile.email, 'refCode:', userProfile.refCode, 'role:', userProfile.role);
      
      if (userProfile.role === 'admin') {
        // Admins see their own referral leads + homepage leads
        // Using 'in' query without orderBy to avoid composite index requirement
        q = query(
          leadsRef, 
          where('refCode', 'in', [userProfile.refCode, 'homepage'])
        );
      } else {
        // Regular users see only their own referral leads
        // No orderBy to avoid composite index requirement
        q = query(
          leadsRef, 
          where('refCode', '==', userProfile.refCode)
        );
      }
      
      const querySnapshot = await getDocs(q);
      console.log('Query returned', querySnapshot.docs.length, 'leads');
      
      let leadsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
      }));
      
      // Sort client-side (since we can't use orderBy with complex queries)
      leadsData.sort((a, b) => b.createdAt - a.createdAt);
      
      setLeads(leadsData);
    } catch (err) {
      console.error('Error loading leads:', err);
      console.error('Error details:', err.code, err.message);
    } finally {
      setLeadsLoading(false);
    }
  };

  // Load all users for reassignment
  const loadUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const querySnapshot = await getDocs(usersRef);
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllUsers(usersData);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  // Load leads and users when userProfile is available
  useEffect(() => {
    if (userProfile) {
      loadLeads();
      loadUsers();
    }
  }, [userProfile]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/admin');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopySnackbar(true);
  };

  const getReferralLink = () => {
    return `${window.location.origin}/estimate?ref=${userProfile?.refCode}`;
  };

  // Format answer labels for display (using translations)
  const formatAnswerLabel = (key, value) => {
    const labels = {
      accidentType: {
        label: t.admin.accidentTypeLabel,
        values: t.accidentType.options
      },
      erVisit: {
        label: t.admin.erVisitLabel,
        values: t.erVisit.options
      },
      injurySeverity: {
        label: t.admin.injurySeverityLabel,
        values: t.injurySeverity.options
      },
      missedWork: {
        label: t.admin.missedWorkLabel,
        values: t.missedWork.options
      },
      fault: {
        label: t.admin.faultLabel,
        values: t.fault.options
      },
      policeReport: {
        label: t.admin.policeReportLabel,
        values: t.policeReport.options
      },
      city: {
        label: t.admin.cityLabel,
        values: null // free text
      },
      state: {
        label: t.admin.stateLabel,
        values: null // state code
      }
    };
    
    const config = labels[key];
    if (!config) return { label: key, value: value };
    
    return {
      label: config.label,
      value: config.values ? (config.values[value] || value) : value
    };
  };

  const handleViewLead = async (lead) => {
    setSelectedLead(lead);
    setLeadNotes(lead.notes || '');
    setNotesSaved(false);
    setLeadDetailDialog(true);
    
    // Auto-update status from 'new' to 'read' when viewing
    if (lead.status === 'new' || !lead.status) {
      try {
        await updateDoc(doc(db, 'leads', lead.id), {
          status: 'read',
          readAt: new Date(),
        });
        // Update local state
        setLeads(prevLeads => 
          prevLeads.map(l => 
            l.id === lead.id ? { ...l, status: 'read', readAt: new Date() } : l
          )
        );
        setSelectedLead({ ...lead, status: 'read', readAt: new Date() });
      } catch (err) {
        console.error('Error updating lead status:', err);
      }
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedLead) return;
    
    setNotesSaving(true);
    try {
      await updateDoc(doc(db, 'leads', selectedLead.id), {
        notes: leadNotes,
        notesUpdatedAt: new Date(),
      });
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(l => 
          l.id === selectedLead.id ? { ...l, notes: leadNotes, notesUpdatedAt: new Date() } : l
        )
      );
      setSelectedLead({ ...selectedLead, notes: leadNotes, notesUpdatedAt: new Date() });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (err) {
      console.error('Error saving notes:', err);
    } finally {
      setNotesSaving(false);
    }
  };

  const handleMarkAsReferral = async () => {
    if (!selectedLead) return;
    
    try {
      await updateDoc(doc(db, 'leads', selectedLead.id), {
        status: 'referral',
        referredAt: new Date(),
      });
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(l => 
          l.id === selectedLead.id ? { ...l, status: 'referral', referredAt: new Date() } : l
        )
      );
      setSelectedLead({ ...selectedLead, status: 'referral', referredAt: new Date() });
    } catch (err) {
      console.error('Error marking lead as referral:', err);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      new: { label: t.admin.statusNew, bgcolor: '#4A9E7F', color: '#FFFFFF' },
      read: { label: t.admin.statusRead, bgcolor: '#C6D8D3', color: '#3A3335' },
      referral: { label: t.admin.statusReferral, bgcolor: '#D81E5B', color: '#FFFFFF' },
    };
    const config = statusConfig[status] || statusConfig.new;
    return (
      <Chip 
        label={config.label} 
        size="small" 
        sx={{ bgcolor: config.bgcolor, color: config.color, fontWeight: 600 }} 
      />
    );
  };

  // Delete single lead
  const handleDeleteLead = async () => {
    if (!selectedLead) return;
    
    setActionLoading(true);
    try {
      await deleteDoc(doc(db, 'leads', selectedLead.id));
      // Remove from local state
      setLeads(prevLeads => prevLeads.filter(l => l.id !== selectedLead.id));
      setDeleteConfirmDialog(false);
      setLeadDetailDialog(false);
      setSelectedLead(null);
    } catch (err) {
      console.error('Error deleting lead:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk selection handlers
  const handleSelectLead = (leadId, event) => {
    event.stopPropagation();
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(paginatedLeads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const isAllSelected = paginatedLeads.length > 0 && paginatedLeads.every(lead => selectedLeads.includes(lead.id));
  const isSomeSelected = selectedLeads.length > 0 && !isAllSelected;

  // Bulk mark as referral
  const handleBulkMarkReferral = async () => {
    setActionLoading(true);
    try {
      await Promise.all(
        selectedLeads.map(leadId => 
          updateDoc(doc(db, 'leads', leadId), {
            status: 'referral',
            referredAt: new Date(),
          })
        )
      );
      // Update local state
      setLeads(prevLeads => 
        prevLeads.map(l => 
          selectedLeads.includes(l.id) 
            ? { ...l, status: 'referral', referredAt: new Date() } 
            : l
        )
      );
      setSelectedLeads([]);
    } catch (err) {
      console.error('Error bulk updating leads:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    setActionLoading(true);
    try {
      await Promise.all(
        selectedLeads.map(leadId => deleteDoc(doc(db, 'leads', leadId)))
      );
      // Remove from local state
      setLeads(prevLeads => prevLeads.filter(l => !selectedLeads.includes(l.id)));
      setSelectedLeads([]);
      setBulkDeleteConfirmDialog(false);
    } catch (err) {
      console.error('Error bulk deleting leads:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk reassign leads
  const handleBulkReassign = async () => {
    if (!reassignTargetUser) return;
    
    setActionLoading(true);
    try {
      const targetUser = allUsers.find(u => u.id === reassignTargetUser);
      if (!targetUser) return;
      
      await Promise.all(
        selectedLeads.map(leadId => 
          updateDoc(doc(db, 'leads', leadId), {
            refCode: targetUser.refCode,
            reassignedAt: new Date(),
            reassignedBy: user.uid,
          })
        )
      );
      
      // Update local state - remove reassigned leads from view if they no longer belong to this user
      if (userProfile.role !== 'admin') {
        // Regular users: remove leads that are now assigned to someone else
        setLeads(prevLeads => prevLeads.filter(l => !selectedLeads.includes(l.id)));
      } else {
        // Admins: update the refCode in local state, but only keep if it's their refCode or homepage
        setLeads(prevLeads => 
          prevLeads.map(l => {
            if (selectedLeads.includes(l.id)) {
              return { ...l, refCode: targetUser.refCode, reassignedAt: new Date() };
            }
            return l;
          }).filter(l => 
            l.refCode === userProfile.refCode || l.refCode === 'homepage' || !selectedLeads.includes(l.id)
          )
        );
      }
      
      setSelectedLeads([]);
      setReassignDialogOpen(false);
      setReassignTargetUser('');
      setReassignSnackbar(true);
    } catch (err) {
      console.error('Error reassigning leads:', err);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          bgcolor: '#FDF0D5', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}
      >
        <CircularProgress sx={{ color: '#EB5E55' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FDF0D5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#FFFFFF', borderBottom: '1px solid #C6D8D3', py: { xs: 1.5, sm: 2 } }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 1.5, md: 0 },
          }}>
            <Typography 
              variant={isMobile ? 'h6' : 'h5'}
              sx={{ 
                fontWeight: 700,
                color: '#3A3335',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {settings.platformName}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1, sm: 2 },
              flexWrap: 'wrap',
              justifyContent: { xs: 'space-between', md: 'flex-end' },
            }}>
              {/* Language Toggle */}
              <ToggleButtonGroup
                value={language}
                exclusive
                onChange={handleLanguageChange}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    color: '#5C5658',
                    borderColor: '#C6D8D3',
                    py: 0.5,
                    px: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&.Mui-selected': {
                      color: '#EB5E55',
                      bgcolor: 'rgba(235, 94, 85, 0.08)',
                      borderColor: '#EB5E55',
                    },
                  },
                }}
              >
                <ToggleButton value="en">EN</ToggleButton>
                <ToggleButton value="es">ES</ToggleButton>
              </ToggleButtonGroup>
              
              {/* Email - hide on mobile */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#5C5658',
                  display: { xs: 'none', sm: 'block' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  maxWidth: { sm: '120px', md: 'none' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.email}
              </Typography>
              
              {userProfile?.role === 'admin' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={!isMobile && <SettingsIcon />}
                  onClick={() => navigate('/admin/dashboard')}
                  sx={{ 
                    borderColor: '#C6D8D3',
                    color: '#5C5658',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 0.5, sm: 0.75 },
                    px: { xs: 1, sm: 2 },
                    minWidth: { xs: 'auto', sm: '100px' },
                    '&:hover': { 
                      borderColor: '#EB5E55',
                      color: '#EB5E55',
                      bgcolor: 'rgba(235, 94, 85, 0.08)' 
                    }
                  }}
                >
                  {isMobile ? <SettingsIcon fontSize="small" /> : t.admin.adminButton}
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                startIcon={!isMobile && <LogoutIcon />}
                onClick={handleLogout}
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  py: { xs: 0.5, sm: 0.75 },
                  px: { xs: 1, sm: 2 },
                  minWidth: { xs: 'auto', sm: '100px' },
                }}
              >
                {isMobile ? <LogoutIcon fontSize="small" /> : t.admin.logout}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
        {/* Referral Link Card */}
        <Paper sx={{ p: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, bgcolor: '#FFFFFF', border: '2px solid #EB5E55', borderRadius: '16px' }}>
          <Typography variant="h6" sx={{ mb: { xs: 1, sm: 2 }, color: '#D81E5B', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t.admin.yourReferralLink}
          </Typography>
          <Typography variant="body2" sx={{ mb: { xs: 2, sm: 3 }, color: '#5C5658', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {t.admin.referralLinkDescription}
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              p: 2,
              bgcolor: 'rgba(235, 94, 85, 0.08)',
              borderRadius: '10px',
              border: '1px solid rgba(235, 94, 85, 0.3)',
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                flex: 1, 
                color: '#3A3335',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {getReferralLink()}
            </Typography>
            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(getReferralLink())}
              sx={{ 
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              }}
            >
              {t.admin.copyLink}
            </Button>
          </Box>
        </Paper>

        {/* Leads Table */}
        <Paper sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#FFFFFF', border: '1px solid #C6D8D3', borderRadius: '16px' }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', md: 'center' }, 
            mb: 3, 
            gap: 2 
          }}>
            <Typography variant="h6" sx={{ color: '#3A3335', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {t.admin.yourLeads} ({filteredLeads.length})
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' }, 
              gap: { xs: 1.5, sm: 2 } 
            }}>
              {/* Search Input */}
              <TextField
                size="small"
                placeholder={t.admin.searchPlaceholder}
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ 
                  minWidth: { xs: '100%', sm: 200, md: 280 },
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: '10px',
                    bgcolor: 'rgba(253, 240, 213, 0.5)',
                    '& fieldset': { borderColor: '#C6D8D3' },
                    '&:hover fieldset': { borderColor: '#EB5E55' },
                    '&.Mui-focused fieldset': { borderColor: '#EB5E55' },
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#5C5658' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton onClick={loadLeads} disabled={leadsLoading} sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                <RefreshIcon sx={{ color: '#EB5E55' }} />
              </IconButton>
            </Box>
          </Box>

          {leadsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#EB5E55' }} />
            </Box>
          ) : leads.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" sx={{ color: '#5C5658', mb: 2 }}>
                {t.admin.noLeadsYet}
              </Typography>
              <Typography variant="body2" sx={{ color: '#7A7577' }}>
                {t.admin.shareToCollect}
              </Typography>
            </Box>
          ) : filteredLeads.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" sx={{ color: '#5C5658', mb: 2 }}>
                {t.admin.noResults}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Bulk Action Bar */}
              {selectedLeads.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  mb: 2, 
                  p: 2, 
                  bgcolor: 'rgba(235, 94, 85, 0.08)', 
                  borderRadius: '10px',
                  border: '1px solid rgba(235, 94, 85, 0.3)',
                }}>
                  <Typography variant="body2" sx={{ color: '#3A3335', fontWeight: 600 }}>
                    {t.admin.selectedCount.replace('{count}', selectedLeads.length)}
                  </Typography>
                  <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={!isMobile && <SwapHorizIcon />}
                      onClick={() => setReassignDialogOpen(true)}
                      disabled={actionLoading}
                      sx={{ 
                        borderColor: '#4A90E2', 
                        color: '#4A90E2',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 0.75 },
                        '&:hover': { bgcolor: 'rgba(74, 144, 226, 0.08)', borderColor: '#4A90E2' }
                      }}
                    >
                      {isMobile ? 'Reassign' : t.admin.reassignLead}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleBulkMarkReferral}
                      disabled={actionLoading}
                      sx={{ 
                        borderColor: '#D81E5B', 
                        color: '#D81E5B',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 0.75 },
                        '&:hover': { bgcolor: 'rgba(216, 30, 91, 0.08)', borderColor: '#D81E5B' }
                      }}
                    >
                      {isMobile ? 'Referral' : t.admin.bulkMarkReferral}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={!isMobile && <DeleteIcon />}
                      onClick={() => setBulkDeleteConfirmDialog(true)}
                      disabled={actionLoading}
                      sx={{ 
                        borderColor: '#DC2626', 
                        color: '#DC2626',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 0.75 },
                        '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.08)', borderColor: '#DC2626' }
                      }}
                    >
                      {isMobile ? <DeleteIcon fontSize="small" /> : t.admin.bulkDelete}
                    </Button>
                  </Box>
                </Box>
              )}
              
              {/* Mobile/Tablet: Card-based layout */}
              {(isMobile || isTablet) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Select All on mobile */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid #C6D8D3' }}>
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isSomeSelected}
                      onChange={handleSelectAll}
                      size="small"
                      sx={{
                        color: '#C6D8D3',
                        '&.Mui-checked': { color: '#EB5E55' },
                        '&.MuiCheckbox-indeterminate': { color: '#EB5E55' },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: '#5C5658' }}>
                      Select All
                    </Typography>
                  </Box>
                  
                  {paginatedLeads.map((lead) => (
                    <Card 
                      key={lead.id} 
                      onClick={() => handleViewLead(lead)}
                      sx={{ 
                        bgcolor: selectedLeads.includes(lead.id) 
                          ? 'rgba(235, 94, 85, 0.08)' 
                          : (lead.status === 'new' || !lead.status) 
                            ? 'rgba(74, 158, 127, 0.08)' 
                            : 'rgba(253, 240, 213, 0.3)',
                        border: '1px solid #C6D8D3',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#EB5E55' },
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        {/* Top row: Checkbox, Name, Status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                              checked={selectedLeads.includes(lead.id)}
                              onChange={(e) => handleSelectLead(lead.id, e)}
                              onClick={(e) => e.stopPropagation()}
                              size="small"
                              sx={{
                                color: '#C6D8D3',
                                p: 0,
                                '&.Mui-checked': { color: '#EB5E55' },
                              }}
                            />
                            <Typography variant="subtitle1" sx={{ color: '#3A3335', fontWeight: (lead.status === 'new' || !lead.status) ? 600 : 500, fontSize: '0.95rem' }}>
                              {lead.name}
                            </Typography>
                          </Box>
                          {getStatusChip(lead.status || 'new')}
                        </Box>
                        
                        {/* Contact info */}
                        <Box sx={{ pl: 3.5, mb: 1.5 }}>
                          <Typography variant="body2" sx={{ color: '#5C5658', fontSize: '0.85rem' }}>
                            📞 {lead.phone}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#5C5658', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            ✉️ {lead.email}
                          </Typography>
                        </Box>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        {/* Settlement range and metadata */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: '#D81E5B', fontWeight: 600 }}>
                            {lead.range?.formattedMin} – {lead.range?.formattedMax}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {userProfile?.role === 'admin' && (
                              <Chip 
                                label={lead.refCode === 'homepage' ? 'HP' : lead.refCode} 
                                size="small" 
                                sx={{ 
                                  bgcolor: lead.refCode === 'homepage' ? '#FEF3C7' : 'rgba(235, 94, 85, 0.1)', 
                                  color: lead.refCode === 'homepage' ? '#92400E' : '#EB5E55',
                                  fontSize: '0.65rem',
                                  height: '20px',
                                }} 
                              />
                            )}
                            <Typography variant="caption" sx={{ color: '#7A7577' }}>
                              {lead.createdAt?.toLocaleDateString?.() || '—'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                /* Desktop: Table layout */
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onChange={handleSelectAll}
                            sx={{
                              color: '#C6D8D3',
                              '&.Mui-checked': { color: '#EB5E55' },
                              '&.MuiCheckbox-indeterminate': { color: '#EB5E55' },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.status}</TableCell>
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.name}</TableCell>
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.phone}</TableCell>
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.email}</TableCell>
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.settlementRange}</TableCell>
                        {userProfile?.role === 'admin' && (
                          <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.refCode}</TableCell>
                        )}
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.city}</TableCell>
                        <TableCell sx={{ color: '#5C5658', fontWeight: 600 }}>{t.admin.date}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedLeads.map((lead) => (
                        <TableRow 
                          key={lead.id} 
                          sx={{ 
                            '&:hover': { bgcolor: 'rgba(253, 240, 213, 0.5)', cursor: 'pointer' },
                            bgcolor: selectedLeads.includes(lead.id) 
                              ? 'rgba(235, 94, 85, 0.08)' 
                              : (lead.status === 'new' || !lead.status) 
                                ? 'rgba(74, 158, 127, 0.08)' 
                                : 'transparent',
                          }}
                          onClick={() => handleViewLead(lead)}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedLeads.includes(lead.id)}
                              onChange={(e) => handleSelectLead(lead.id, e)}
                              sx={{
                                color: '#C6D8D3',
                                '&.Mui-checked': { color: '#EB5E55' },
                              }}
                            />
                          </TableCell>
                          <TableCell>{getStatusChip(lead.status || 'new')}</TableCell>
                          <TableCell sx={{ color: '#3A3335', fontWeight: (lead.status === 'new' || !lead.status) ? 600 : 400 }}>{lead.name}</TableCell>
                          <TableCell sx={{ color: '#3A3335' }}>{lead.phone}</TableCell>
                          <TableCell sx={{ color: '#3A3335' }}>{lead.email}</TableCell>
                          <TableCell sx={{ color: '#D81E5B', fontWeight: 600 }}>
                            {lead.range?.formattedMin} – {lead.range?.formattedMax}
                          </TableCell>
                          {userProfile?.role === 'admin' && (
                            <TableCell>
                              <Chip 
                                label={lead.refCode === 'homepage' ? t.admin.homepage : lead.refCode} 
                                size="small" 
                                sx={{ 
                                  bgcolor: lead.refCode === 'homepage' ? '#FEF3C7' : 'rgba(235, 94, 85, 0.1)', 
                                  color: lead.refCode === 'homepage' ? '#92400E' : '#EB5E55' 
                                }} 
                              />
                            </TableCell>
                          )}
                          <TableCell sx={{ color: '#5C5658' }}>
                            {lead.answers?.city || '—'}
                          </TableCell>
                          <TableCell sx={{ color: '#5C5658' }}>
                            {lead.createdAt?.toLocaleDateString?.() || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          
          {/* Pagination Controls */}
          {filteredLeads.length > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: 3,
              pt: 3,
              borderTop: '1px solid #C6D8D3',
              flexWrap: 'wrap',
              gap: 2,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#5C5658' }}>
                  {t.admin.showing} {startIndex + 1}–{Math.min(endIndex, filteredLeads.length)} {t.admin.of} {filteredLeads.length}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Rows per page selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    sx={{ 
                      minWidth: 70,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C6D8D3' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#EB5E55' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#EB5E55' },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                  </Select>
                  <Typography variant="body2" sx={{ color: '#5C5658' }}>
                    {t.admin.leadsPerPage}
                  </Typography>
                </Box>
                
                {/* Page navigation */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    sx={{ 
                      color: currentPage === 1 ? '#C6D8D3' : '#EB5E55',
                      '&:hover': { bgcolor: 'rgba(235, 94, 85, 0.08)' }
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ color: '#3A3335', minWidth: 80, textAlign: 'center' }}>
                    {currentPage} / {totalPages || 1}
                  </Typography>
                  <IconButton 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    sx={{ 
                      color: currentPage >= totalPages ? '#C6D8D3' : '#EB5E55',
                      '&:hover': { bgcolor: 'rgba(235, 94, 85, 0.08)' }
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Lead Detail Dialog */}
      <Dialog 
        open={leadDetailDialog} 
        onClose={() => setLeadDetailDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: isMobile ? 'none' : '1px solid #C6D8D3',
            borderRadius: isMobile ? 0 : '12px',
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <DialogTitle sx={{ color: '#3A3335', borderBottom: '1px solid #C6D8D3' }}>
          {t.admin.leadDetails}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedLead && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Contact Information */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {t.admin.contactInformation}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', border: '1px solid #C6D8D3' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.name}</Typography>
                      <Typography variant="body1" sx={{ color: '#3A3335', fontWeight: 500 }}>{selectedLead.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.phone}</Typography>
                      <Typography variant="body1" sx={{ color: '#3A3335', fontWeight: 500 }}>{selectedLead.phone}</Typography>
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.email}</Typography>
                      <Typography variant="body1" sx={{ color: '#3A3335', fontWeight: 500 }}>{selectedLead.email}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Settlement Range */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {t.admin.estimatedSettlementRange}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(235, 94, 85, 0.08)', border: '1px solid rgba(235, 94, 85, 0.3)' }}>
                  <Typography variant="h5" sx={{ color: '#D81E5B', fontWeight: 700, textAlign: 'center' }}>
                    {selectedLead.range?.formattedMin} – {selectedLead.range?.formattedMax}
                  </Typography>
                </Paper>
              </Box>

              {/* Form Answers */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {t.admin.formResponses}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', border: '1px solid #C6D8D3' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {selectedLead.answers && Object.entries(selectedLead.answers).map(([key, value]) => {
                      const formatted = formatAnswerLabel(key, value);
                      return (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #C6D8D3', '&:last-child': { borderBottom: 'none' } }}>
                          <Typography variant="body2" sx={{ color: '#5C5658' }}>{formatted.label}</Typography>
                          <Typography variant="body2" sx={{ color: '#3A3335', fontWeight: 500, textAlign: 'right' }}>{formatted.value || '—'}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Box>

              {/* Metadata */}
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#5C5658', mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                  {t.admin.submissionDetails}
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'rgba(253, 240, 213, 0.5)', border: '1px solid #C6D8D3' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.submitted}</Typography>
                      <Typography variant="body2" sx={{ color: '#3A3335' }}>
                        {selectedLead.createdAt?.toLocaleString?.() || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.language}</Typography>
                      <Typography variant="body2" sx={{ color: '#3A3335' }}>
                        {selectedLead.language === 'es' ? 'Español' : 'English'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.referralCode}</Typography>
                      <Chip 
                        label={selectedLead.refCode === 'homepage' ? t.admin.homepage : selectedLead.refCode} 
                        size="small" 
                        sx={{ 
                          mt: 0.5,
                          bgcolor: selectedLead.refCode === 'homepage' ? '#FEF3C7' : 'rgba(235, 94, 85, 0.1)', 
                          color: selectedLead.refCode === 'homepage' ? '#92400E' : '#EB5E55' 
                        }} 
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#5C5658' }}>{t.admin.consentGiven}</Typography>
                      <Typography variant="body2" sx={{ color: selectedLead.consentGiven ? '#4A9E7F' : '#D81E5B' }}>
                        {selectedLead.consentGiven ? `✓ ${t.admin.yes}` : `✗ ${t.admin.no}`}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Notes */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ color: '#5C5658', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                    {t.admin.notes}
                  </Typography>
                  {notesSaved && (
                    <Chip 
                      label={`✓ ${t.admin.notesSaved}`} 
                      size="small" 
                      sx={{ bgcolor: 'rgba(74, 158, 127, 0.15)', color: '#3A6B5A', fontSize: '0.7rem' }} 
                    />
                  )}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={t.admin.notesPlaceholder}
                  value={leadNotes}
                  onChange={(e) => setLeadNotes(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      bgcolor: '#FFFFFF',
                      '& fieldset': { borderColor: '#C6D8D3' },
                      '&:hover fieldset': { borderColor: '#EB5E55' },
                      '&.Mui-focused fieldset': { borderColor: '#EB5E55' },
                    },
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSaveNotes}
                  disabled={notesSaving || leadNotes === (selectedLead?.notes || '')}
                  sx={{ 
                    mt: 1.5,
                    borderColor: '#EB5E55',
                    color: '#EB5E55',
                    '&:hover': { bgcolor: 'rgba(235, 94, 85, 0.08)', borderColor: '#EB5E55' },
                    '&.Mui-disabled': { borderColor: '#C6D8D3', color: '#C6D8D3' },
                  }}
                >
                  {notesSaving ? 'Saving...' : t.admin.saveNotes}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #C6D8D3', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedLead?.status !== 'referral' && (
              <Button 
                onClick={handleMarkAsReferral} 
                variant="contained"
                sx={{ 
                  bgcolor: '#D81E5B', 
                  '&:hover': { bgcolor: '#B8184D' },
                }}
              >
                {t.admin.markAsReferral}
              </Button>
            )}
            {selectedLead?.status === 'referral' && (
              <Chip 
                label={`✓ ${t.admin.markedAsReferral}`} 
                sx={{ bgcolor: '#D81E5B', color: '#FFFFFF', fontWeight: 600 }} 
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={() => setDeleteConfirmDialog(true)} 
              variant="outlined"
              startIcon={<DeleteIcon />}
              sx={{ 
                borderColor: '#DC2626', 
                color: '#DC2626',
                '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.08)', borderColor: '#DC2626' }
              }}
            >
              {t.admin.deleteLead}
            </Button>
            <Button onClick={() => setLeadDetailDialog(false)} variant="outlined">
              {t.admin.close}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#DC2626' }}>
          {t.admin.deleteConfirmTitle}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: '10px' }}>
            {t.admin.deleteConfirmMessage}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmDialog(false)} variant="outlined">
            {t.admin.cancel}
          </Button>
          <Button 
            onClick={handleDeleteLead} 
            variant="contained"
            disabled={actionLoading}
            sx={{ 
              bgcolor: '#DC2626', 
              '&:hover': { bgcolor: '#B91C1C' },
            }}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : t.admin.deleteConfirmButton}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteConfirmDialog}
        onClose={() => setBulkDeleteConfirmDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#DC2626' }}>
          {t.admin.bulkDeleteConfirmTitle}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: '10px' }}>
            {t.admin.bulkDeleteConfirmMessage.replace('{count}', selectedLeads.length)}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkDeleteConfirmDialog(false)} variant="outlined">
            {t.admin.cancel}
          </Button>
          <Button 
            onClick={handleBulkDelete} 
            variant="contained"
            disabled={actionLoading}
            sx={{ 
              bgcolor: '#DC2626', 
              '&:hover': { bgcolor: '#B91C1C' },
            }}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : t.admin.deleteConfirmButton}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog
        open={reassignDialogOpen}
        onClose={() => {
          setReassignDialogOpen(false);
          setReassignTargetUser('');
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: '#FFFFFF',
            border: '1px solid #C6D8D3',
            borderRadius: '12px',
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle sx={{ color: '#3A3335' }}>
          {t.admin.reassignTo}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#5C5658' }}>
            {t.admin.selectedCount.replace('{count}', selectedLeads.length)}
          </Typography>
          <Select
            fullWidth
            value={reassignTargetUser}
            onChange={(e) => setReassignTargetUser(e.target.value)}
            displayEmpty
            sx={{
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C6D8D3' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#EB5E55' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#EB5E55' },
            }}
          >
            <MenuItem value="" disabled>
              {t.admin.selectUser}
            </MenuItem>
            {allUsers.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {u.name || u.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#5C5658' }}>
                    {u.email} • {u.refCode}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setReassignDialogOpen(false);
              setReassignTargetUser('');
            }} 
            variant="outlined"
          >
            {t.admin.cancel}
          </Button>
          <Button 
            onClick={handleBulkReassign} 
            variant="contained"
            disabled={actionLoading || !reassignTargetUser}
            sx={{ 
              bgcolor: '#4A90E2', 
              '&:hover': { bgcolor: '#357ABD' },
            }}
          >
            {actionLoading ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : t.admin.reassignConfirm}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Link copied to clipboard!"
      />

      {/* Reassign Success Snackbar */}
      <Snackbar
        open={reassignSnackbar}
        autoHideDuration={3000}
        onClose={() => setReassignSnackbar(false)}
        message={t.admin.leadsReassigned}
      />
    </Box>
  );
}

export default UserLeadsPage;

